import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import { API, ADMIN_TOKEN_KEY, api, apiError, getAdminToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Users, Activity, ImageIcon, HardDrive, Search, Loader2, ShieldCheck,
  Ban, CheckCircle2, Crown, LogOut, Store, Wallet, Check, X, Clock,
} from "lucide-react";

export default function Admin() {
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [checking, setChecking] = useState(!!getAdminToken());

  useEffect(() => {
    if (getAdminToken()) {
      api.get("/admin/me").then(() => setAuthed(true)).catch(() => { localStorage.removeItem(ADMIN_TOKEN_KEY); setAuthed(false); }).finally(() => setChecking(false));
    }
  }, []);

  if (checking) return <div className="min-h-screen bg-tp-bg flex items-center justify-center"><Loader2 className="animate-spin text-tp-green" /></div>;
  return authed ? <AdminDashboard onLogout={() => setAuthed(false)} /> : <AdminLogin onLogin={() => setAuthed(true)} />;
}

function AdminLogin({ onLogin }) {
  const [mode, setMode] = useState("phone"); // phone | email
  const [telephone, setTelephone] = useState("");
  const [pin, setPin] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      const payload = mode === "phone"
        ? { url: "/admin/login-phone", body: { telephone, pin } }
        : { url: "/admin/login", body: { email, password } };
      const { data } = await axios.post(`${API}${payload.url}`, payload.body);
      localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
      toast.success("Connecté");
      onLogin();
    } catch (e) { toast.error(apiError(e, "Identifiants incorrects")); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-tp-green flex flex-col items-center justify-center px-6">
      <div className="w-16 h-16 rounded-2xl bg-tp-gold flex items-center justify-center mb-5"><ShieldCheck size={32} className="text-tp-ink" /></div>
      <h1 className="font-head text-2xl font-extrabold text-white mb-1">Administration</h1>
      <p className="text-white/70 mb-8">Tailleur Pro · Espace propriétaire</p>
      <div className="w-full max-w-sm space-y-3">
        {mode === "phone" ? (
          <>
            <input data-testid="admin-phone" value={telephone} onChange={(e) => setTelephone(e.target.value)} placeholder="Numéro de portable" type="tel" inputMode="numeric"
              className="w-full h-[52px] rounded-xl px-4 text-base outline-none" />
            <input data-testid="admin-pin" value={pin} onChange={(e) => setPin(e.target.value.replace(/[^\d]/g, "").slice(0, 4))} placeholder="Code PIN (4 chiffres)" type="tel" inputMode="numeric"
              onKeyDown={(e) => e.key === "Enter" && submit()} className="w-full h-[52px] rounded-xl px-4 text-base outline-none tracking-widest" />
          </>
        ) : (
          <>
            <input data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email admin" type="email"
              className="w-full h-[52px] rounded-xl px-4 text-base outline-none" />
            <input data-testid="admin-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mot de passe" type="password"
              onKeyDown={(e) => e.key === "Enter" && submit()} className="w-full h-[52px] rounded-xl px-4 text-base outline-none" />
          </>
        )}
        <Button data-testid="admin-login-btn" onClick={submit} disabled={loading} className="w-full h-[52px] rounded-full bg-tp-gold hover:bg-tp-gold/90 text-tp-ink font-bold">
          {loading ? <Loader2 className="animate-spin" size={20} /> : "Se connecter"}
        </Button>
        <button data-testid="admin-toggle-mode" onClick={() => setMode(mode === "phone" ? "email" : "phone")} className="w-full text-center text-white/70 text-sm py-2">
          {mode === "phone" ? "Connexion par email" : "Connexion par numéro + PIN"}
        </button>
      </div>
    </div>
  );
}


function AdminDashboard({ onLogout }) {
  const [stats, setStats] = useState(null);
  const [tailleurs, setTailleurs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [platform, setPlatform] = useState({ wave_link: "", wave_number: "", om_number: "", pro_price: 2500 });
  const [savingPlatform, setSavingPlatform] = useState(false);
  const [requests, setRequests] = useState([]);

  const loadExtras = useCallback(async () => {
    try {
      const [p, r] = await Promise.all([
        api.get("/admin/platform-settings"),
        api.get("/admin/upgrade-requests", { params: { status: "pending" } }),
      ]);
      setPlatform({ wave_link: p.data.wave_link || "", wave_number: p.data.wave_number || "", om_number: p.data.om_number || "", pro_price: p.data.pro_price || 2500 });
      setRequests(r.data);
    } catch { /* ignore */ }
  }, []);

  const savePlatform = async () => {
    setSavingPlatform(true);
    try {
      await api.put("/admin/platform-settings", platform);
      toast.success("Coordonnées de paiement enregistrées");
    } catch { toast.error("Erreur"); } finally { setSavingPlatform(false); }
  };

  const decide = async (rid, approve) => {
    try {
      await api.post(`/admin/upgrade-requests/${rid}/decide`, { approve, months: 1 });
      setRequests((list) => list.filter((x) => x.id !== rid));
      toast.success(approve ? "Plan Pro activé (1 mois)" : "Demande rejetée");
    } catch { toast.error("Erreur"); }
  };

  const load = useCallback(async (search = "") => {
    try {
      const [s, t] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/tailleurs", { params: search ? { q: search } : {} }),
      ]);
      setStats(s.data);
      setTailleurs(t.data);
    } catch { toast.error("Erreur de chargement"); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); loadExtras(); }, [load, loadExtras]);
  useEffect(() => { const t = setTimeout(() => load(q), 350); return () => clearTimeout(t); }, [q, load]);

  const toggleSuspend = async (t) => {
    try {
      await api.post(`/admin/tailleurs/${t.id}/suspend`, { suspended: !t.suspended });
      setTailleurs((list) => list.map((x) => (x.id === t.id ? { ...x, suspended: !x.suspended } : x)));
      toast.success(!t.suspended ? "Compte suspendu" : "Compte réactivé");
    } catch { toast.error("Erreur"); }
  };
  const togglePlan = async (t) => {
    const plan = t.plan === "pro" ? "gratuit" : "pro";
    try {
      await api.post(`/admin/tailleurs/${t.id}/plan`, { plan });
      setTailleurs((list) => list.map((x) => (x.id === t.id ? { ...x, plan } : x)));
      toast.success(`Plan: ${plan}`);
    } catch { toast.error("Erreur"); }
  };
  const logout = () => { localStorage.removeItem(ADMIN_TOKEN_KEY); onLogout(); };

  return (
    <div className="min-h-screen bg-tp-bg pb-10">
      <div className="bg-tp-green text-white px-5 pt-6 pb-8 rounded-b-3xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><ShieldCheck size={24} className="text-tp-gold" /><h1 className="font-head font-extrabold text-xl">Administration</h1></div>
          <button data-testid="admin-logout" onClick={logout} className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center"><LogOut size={19} /></button>
        </div>
      </div>

      <div className="px-5 -mt-4 grid grid-cols-2 gap-3">
        <AdminStat testId="admin-stat-tailleurs" icon={Users} label="Tailleurs" value={stats?.tailleurs ?? "—"} />
        <AdminStat testId="admin-stat-actifs" icon={Activity} label="Actifs (30j)" value={stats?.actifs_30j ?? "—"} />
        <AdminStat testId="admin-stat-clients" icon={Store} label="Clients créés" value={stats?.clients ?? "—"} />
        <AdminStat testId="admin-stat-photos" icon={ImageIcon} label="Photos" value={stats?.photos ?? "—"} />
      </div>
      <div className="px-5 mt-3">
        <div className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-3">
          <HardDrive size={22} className="text-tp-green" />
          <div><p className="font-head text-xl font-extrabold text-tp-ink">{stats?.volume_mo ?? 0} Mo</p><p className="text-sm text-tp-muted">Volume photos stocké</p></div>
        </div>
      </div>

      {/* Demandes d'abonnement Pro */}
      <div className="px-5 mt-5">
        <p className="font-head font-bold text-tp-ink flex items-center gap-2 mb-2">
          <Crown size={18} className="text-tp-gold" /> Demandes Pro
          {requests.length > 0 && <span className="text-xs bg-tp-gold text-tp-ink rounded-full px-2 py-0.5 font-bold">{requests.length}</span>}
        </p>
        {requests.length === 0 ? (
          <div className="bg-white rounded-xl p-4 border border-gray-100 text-tp-muted text-sm">Aucune demande en attente.</div>
        ) : (
          <div className="space-y-2.5">
            {requests.map((r) => (
              <div key={r.id} data-testid={`upgrade-req-${r.id}`} className="bg-white rounded-xl p-4 border border-tp-gold/40">
                <div className="flex items-center gap-2 mb-1">
                  <Clock size={15} className="text-tp-gold" />
                  <p className="font-semibold text-tp-ink flex-1 min-w-0 truncate">{r.tailleur_nom} {r.atelier && <span className="text-tp-muted font-normal">· {r.atelier}</span>}</p>
                </div>
                <p className="text-sm text-tp-muted">{r.tailleur_tel} · {r.amount?.toLocaleString("fr-FR")} FCFA · {r.method === "wave" ? "Wave" : "Orange Money"}</p>
                <div className="flex gap-2 mt-3">
                  <button data-testid={`approve-${r.id}`} onClick={() => decide(r.id, true)} className="flex-1 h-10 rounded-lg bg-green-100 text-green-700 text-sm font-semibold flex items-center justify-center gap-1.5">
                    <Check size={16} /> Activer Pro
                  </button>
                  <button data-testid={`reject-${r.id}`} onClick={() => decide(r.id, false)} className="flex-1 h-10 rounded-lg bg-red-50 text-red-600 text-sm font-semibold flex items-center justify-center gap-1.5">
                    <X size={16} /> Rejeter
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Coordonnées de paiement de la plateforme */}
      <div className="px-5 mt-5">
        <div className="bg-white rounded-xl p-4 border border-gray-100 space-y-3">
          <p className="font-head font-bold text-tp-ink flex items-center gap-2"><Wallet size={18} className="text-tp-green" /> Paiement des abonnements</p>
          <p className="text-sm text-tp-muted -mt-1">Vos coordonnées où les tailleurs paient leur abonnement Pro.</p>
          <PField label="Lien de paiement Wave" testId="platform-wave-link" value={platform.wave_link} onChange={(v) => setPlatform({ ...platform, wave_link: v })} placeholder="https://pay.wave.com/..." />
          <PField label="Numéro Wave" testId="platform-wave-number" value={platform.wave_number} onChange={(v) => setPlatform({ ...platform, wave_number: v })} placeholder="+221..." />
          <PField label="Numéro Orange Money" testId="platform-om-number" value={platform.om_number} onChange={(v) => setPlatform({ ...platform, om_number: v })} placeholder="+221..." />
          <PField label="Prix Pro (FCFA/mois)" testId="platform-price" value={String(platform.pro_price)} onChange={(v) => setPlatform({ ...platform, pro_price: Number(v.replace(/[^\d]/g, "")) || 0 })} placeholder="2500" />
          <Button data-testid="save-platform-btn" onClick={savePlatform} disabled={savingPlatform} className="w-full h-12 rounded-full bg-tp-green hover:bg-tp-greenDark font-bold">
            {savingPlatform ? <Loader2 className="animate-spin" size={18} /> : "Enregistrer"}
          </Button>
        </div>
      </div>

      <div className="px-5 mt-5 relative">
        <Search size={20} className="absolute left-8 top-1/2 -translate-y-1/2 text-tp-muted" />
        <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher par numéro, nom ou atelier…"
          className="w-full h-[52px] rounded-xl bg-white border border-gray-300 pl-11 pr-4 text-base outline-none focus:border-tp-green" />
      </div>

      <div className="px-5 mt-4 space-y-2.5">
        {loading ? <div className="flex justify-center py-8"><Loader2 className="animate-spin text-tp-green" /></div>
          : tailleurs.length === 0 ? <p className="text-center text-tp-muted py-8">Aucun tailleur</p>
          : tailleurs.map((t) => (
            <div key={t.id} data-testid={`admin-tailleur-${t.id}`} className="bg-white rounded-xl p-4 border border-gray-100">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="font-semibold text-tp-ink truncate">{t.nom} {t.atelier && <span className="text-tp-muted font-normal">· {t.atelier}</span>}</p>
                  <p className="text-sm text-tp-muted">{t.telephone} · {t.clients} clients · {t.ville || "—"}</p>
                </div>
                {t.suspended && <span className="text-xs bg-red-100 text-red-700 rounded-full px-2 py-1 font-semibold shrink-0">Suspendu</span>}
              </div>
              <div className="flex gap-2 mt-3">
                <button data-testid={`admin-plan-${t.id}`} onClick={() => togglePlan(t)}
                  className={`flex-1 h-10 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 ${t.plan === "pro" ? "bg-tp-gold/20 text-tp-gold" : "bg-gray-100 text-tp-muted"}`}>
                  <Crown size={16} /> {t.plan === "pro" ? "Pro" : "Gratuit"}
                </button>
                <button data-testid={`admin-suspend-${t.id}`} onClick={() => toggleSuspend(t)}
                  className={`flex-1 h-10 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 ${t.suspended ? "bg-green-100 text-green-700" : "bg-red-50 text-red-600"}`}>
                  {t.suspended ? <><CheckCircle2 size={16} /> Réactiver</> : <><Ban size={16} /> Suspendre</>}
                </button>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

function AdminStat({ icon: Icon, label, value, testId }) {
  return (
    <div data-testid={testId} className="bg-white rounded-xl p-4 border border-gray-100">
      <Icon size={20} className="text-tp-green mb-1.5" />
      <p className="font-head text-2xl font-extrabold text-tp-ink">{value}</p>
      <p className="text-sm text-tp-muted">{label}</p>
    </div>
  );
}

function PField({ label, value, onChange, testId, placeholder }) {
  return (
    <div>
      <label className="font-semibold text-tp-ink block mb-1 text-sm">{label}</label>
      <input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full h-11 rounded-xl bg-white border border-gray-300 px-4 text-base outline-none focus:border-tp-green" />
    </div>
  );
}
